import React from 'react'
// libs
import { useSelector } from 'react-redux'
import { formValueSelector, reduxForm } from 'redux-form'
import { useMutation, useQuery } from '@apollo/client/react'
// components
import { Button, BUTTON_LAYOUTS } from 'components/UI'
import { ReduxInput, ReduxSelect } from 'components/FormElements/ReduxForm'
// constants
import { Forms } from 'constants/forms'
import { ENVIRONMENT_OPTIONS } from './constants'
// graphql
import { INJURY_MASTER_SETTINGS_UPDATE_MUTATION } from 'graphql/injuryMaster/mutations'
import {
  INJURY_MASTER_COMPANIES_QUERY,
  INJURY_MASTER_SCHEMES_QUERY,
  INJURY_MASTER_SETTINGS_QUERY
} from 'graphql/injuryMaster/queries'
// types
import { IOption } from 'components/FormElements/Select'
import { IInjuryMasterCompany, IInjuryMasterScheme } from 'types/graphql'
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
// views
import { Actions, Form } from './views'
// utils
import { handleError } from 'utils/errors/handlers'
import { required } from 'utils/validators'

const requiredValidators = [required]

interface Props {
  hideModal: () => void
  isConnected: boolean
}

const SettingsForm: React.FC<InjectedFormProps<any, Props> & Props> = ({
  valid,
  change,
  hideModal,
  submitting,
  isConnected,
  handleSubmit
}) => {
  const [updateSettings] = useMutation(INJURY_MASTER_SETTINGS_UPDATE_MUTATION)

  // Companies come from the saved connection, so they can only be listed once one exists.
  const {
    data,
    loading,
    error: companiesError
  } = useQuery<{ injuryMasterCompanies: IInjuryMasterCompany[] }>(INJURY_MASTER_COMPANIES_QUERY, {
    skip: !isConnected,
    fetchPolicy: 'network-only'
  })

  const companyOptions: IOption[] = React.useMemo(
    () => (data?.injuryMasterCompanies ?? []).map(({ id, name }) => ({ value: id, label: name })),
    [data]
  )

  // Schemes too: they belong to the Injury Master account, not to this app, so they aren't hardcoded.
  const {
    data: schemesData,
    loading: schemesLoading,
    error: schemesError
  } = useQuery<{ injuryMasterSchemes: IInjuryMasterScheme[] }>(INJURY_MASTER_SCHEMES_QUERY, {
    skip: !isConnected,
    fetchPolicy: 'network-only'
  })

  const schemeOptions: IOption[] = React.useMemo(
    () =>
      (schemesData?.injuryMasterSchemes ?? []).map(({ id, name }) => ({
        value: String(id),
        label: name
      })),
    [schemesData]
  )

  const scheme: IOption | undefined = useSelector((state) =>
    formValueSelector(Forms.INJURY_MASTER_SETTINGS_FORM)(state, 'schemeId')
  )

  // Same as the company: the saved settings only hold the scheme id.
  React.useEffect(() => {
    if (!scheme || scheme.label !== scheme.value) {
      return
    }

    const match = schemeOptions.find(({ value }) => value === scheme.value)

    if (match) {
      change('schemeId', match)
    }
  }, [change, scheme, schemeOptions])

  const company: IOption | undefined = useSelector((state) =>
    formValueSelector(Forms.INJURY_MASTER_SETTINGS_FORM)(state, 'companyId')
  )

  // The saved settings only hold the company id — show its name once the list is available.
  React.useEffect(() => {
    if (!company || company.label !== company.value) {
      return
    }

    const match = companyOptions.find(({ value }) => value === company.value)

    if (match) {
      change('companyId', match)
    }
  }, [change, company, companyOptions])

  const handleFormSubmit = async (formData: any) => {
    try {
      await updateSettings({
        variables: {
          input: {
            accountId: formData.accountId,
            environment: formData.environment.value,
            bearerToken: formData.bearerToken || undefined,
            companyId: formData.companyId?.value,
            schemeId: formData.schemeId ? Number(formData.schemeId.value) : undefined
          }
        },
        refetchQueries: [INJURY_MASTER_SETTINGS_QUERY]
      })

      // A first save keeps the drawer open: the company dropdown only works once connected.
      if (isConnected) {
        hideModal()
      }
    } catch (error) {
      handleError(error, 'InjuryMasterSettingsModal.handleFormSubmit')
    }
  }

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <ReduxSelect label='Environment' name='environment' options={ENVIRONMENT_OPTIONS} validate={requiredValidators} />
      <ReduxInput name='accountId' label='Account Id' validate={requiredValidators} />
      <ReduxInput
        name='bearerToken'
        label={'Bearer Token'}
        validate={isConnected ? undefined : requiredValidators}
        helperText={isConnected ? 'Bearer Token (leave blank to keep the current one)' : undefined}
      />
      <ReduxSelect
        isClearable
        name='companyId'
        label='Company'
        options={companyOptions}
        isLoading={loading}
        isDisabled={!isConnected}
        errorText={
          companiesError
            ? 'Could not load companies from Injury Master. Check the environment, account id and token'
            : undefined
        }
        helperText={isConnected ? undefined : 'Save the connection first, then choose a company.'}
      />
      <ReduxSelect
        isClearable
        name='schemeId'
        label='Scheme'
        options={schemeOptions}
        isLoading={schemesLoading}
        isDisabled={!isConnected}
        errorText={schemesError ? 'Could not load schemes from Injury Master' : undefined}
        helperText='Leave empty to detect it from the company address.'
      />

      <Actions>
        <Button type='submit' layout={BUTTON_LAYOUTS.PURPLE} disabled={!valid || submitting} isLoading={submitting}>
          Save
        </Button>
        <Button type='button' layout={BUTTON_LAYOUTS.WHITE} onClick={hideModal}>
          Cancel
        </Button>
      </Actions>
    </Form>
  )
}

export default reduxForm<any, Props>({
  form: Forms.INJURY_MASTER_SETTINGS_FORM,
  enableReinitialize: true,
  keepDirtyOnReinitialize: false
})(SettingsForm)
